import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useQuery} from '@apollo/client';

import Empty from '../empty/Empty';
import InfiniteScroll from '../infiniteScroll/InfiniteScroll';

import './CardDeck.css';
import SchemaOrgItemList from "../schemaOrg/SchemaOrgItemList";

const LIMIT = 40;

/**
 * Card deck component.
 */
export default function CardDeck({ children, itemsProperty, query, rowClassName, variables, limit = LIMIT, onlyNetwork = false, isEmptyMsg, schemaOrgPreview, keyword }) {
    // fetch the items feed
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const { loading, data, fetchMore } = useQuery(
        query,
        {
            variables: {
                ...variables,
                offset: 0,
                limit: limit
            },
            fetchPolicy: onlyNetwork ? 'network-only' : 'cache-and-network', // force cache refresh to show new items
            onError: (error) => {
                console.error('Error loading the card deck', error);
            }
        }
    );

    useEffect(() => {
        if (data) {
            setHasMore(data[itemsProperty].length === offset + limit);
        }
    }, [data, itemsProperty, limit]);

    const loadMore = () => {
        const newOffset = data[itemsProperty].length;
        setOffset(newOffset);

        fetchMore({
            variables: {
                offset: newOffset
            },
            updateQuery: (previousResult, { fetchMoreResult }) => ({
                ...previousResult,
                [itemsProperty]: [...previousResult[itemsProperty], ...fetchMoreResult[itemsProperty]]
            })
        });
    };

    // reset the offset on query change
    useEffect(() => setOffset(0), [query, variables]);

    // build the UI elements
    const renderItem = (item, index) => (
        <li className="CardDeck-item col mb-4" key={item.id}>
            {children(item, index)}
        </li>
    );
    const listItems = data && data[itemsProperty] && data[itemsProperty].length ?
        data[itemsProperty].map(renderItem) :
        loading ?
        null :
        (
            <li className="CardDeck-empty">
                {isEmptyMsg && <Empty /> }
            </li>
        );

    return (
        <>
            <div className="CardDeck">
                <InfiniteScroll loading={loading} hasMore={hasMore} loadMore={loadMore}>
                    <ul className={`CardDeck-list list-unstyled row ${rowClassName}`}>
                        {listItems}
                    </ul>
                </InfiniteScroll>
            </div>
            {/* SSR 최초 렌더링 시에만 JSON-LD 출력 */}
            <SchemaOrgItemList
                offset={offset}
                items={data ? data[itemsProperty] : []}
                schemaOrgPreview={schemaOrgPreview}
                keyword={keyword}
            />
        </>
    );
}

CardDeck.propTypes = {
    children: PropTypes.func.isRequired,
    itemsProperty: PropTypes.string.isRequired,
    query: PropTypes.object.isRequired,
    rowClassName: PropTypes.string.isRequired,
    variables: PropTypes.object,
    limit: PropTypes.number,
    onlyNetwork: PropTypes.bool,
    isEmptyMsg: PropTypes.bool,
    schemaOrgPreview: PropTypes.object,
    keyword: PropTypes.string,
};
